"use client";

import clsx from "clsx";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  Bot,
  Check,
  Copy,
  ArrowDown,
  ArrowUp,
  Loader2,
  Maximize2,
  MessageSquareText,
  Minimize2,
  SendHorizonal,
  Sparkles,
  Info,
} from "lucide-react";
import ReactMarkdown from "react-markdown";
import type { Components } from "react-markdown";
import remarkGfm from "remark-gfm";

type MessageRole = "assistant" | "user" | "system";

interface ConversationMessage {
  id: string;
  role: MessageRole;
  content: string;
  createdAt: string;
  type?: "expired_divider" | "expired_prompt";
}

interface Conversation {
  id: string;
  title: string;
  updatedAt: string;
  messages: ConversationMessage[];
  sessionId?: string;
}

interface AgentWorkspaceProps {
  variant: "page" | "popup";
  onMinimize?: () => void;
  onExpand?: () => void;
}

interface MarkdownCodeBlockProps {
  code: string;
  language?: string;
  compact?: boolean;
}

interface AgentWorkspaceState {
  conversation: Conversation;
  input: string;
  isTyping: boolean;
  lastActiveTime: string;
  historyRetention: {
    hasTrimmedHistory: boolean;
    earliestKeptAt?: string;
  };
}

interface AgentWorkspaceSyncDetail {
  sourceId?: string;
  state: AgentWorkspaceState;
}

const SINGLE_CONVERSATION_STORAGE_KEY = "wernicke-agent-single-conversation";
const AGENT_WORKSPACE_SYNC_EVENT = "wernicke-agent-workspace-sync";
const SESSION_TIMEOUT_MS = 60 * 60 * 1000;
const HISTORY_RETENTION_DAYS = 30;
const MAX_PERSISTED_MESSAGES = 120;
const QUICK_STARTS = [
  "我要对接智能 PPT 接口",
  "查询某接口的请求参数",
  "生成完整的对接流程方案",
] as const;

function createId(prefix: string) {
  return `${prefix}-${Math.random().toString(36).slice(2, 10)}`;
}

function createGreetingMessage(now: string): ConversationMessage {
  return {
    id: createId("msg"),
    role: "assistant",
    createdAt: now,
    content:
      "你好，我是韦尼克开放平台的 **AI接入助手**。\n\n我会严格基于平台文档帮助你设计接口调用方案、梳理参数和输出对接流程。你可以直接描述业务目标，或点击下方示例快速开始。",
  };
}

function buildInitialConversation(): Conversation {
  const now = new Date().toISOString();
  return {
    id: createId("conv"),
    title: "AI接入助手",
    updatedAt: now,
    messages: [createGreetingMessage(now)],
  };
}

function buildInitialWorkspaceState(): AgentWorkspaceState {
  const now = new Date().toISOString();
  return {
    conversation: buildInitialConversation(),
    input: "",
    isTyping: false,
    lastActiveTime: now,
    historyRetention: {
      hasTrimmedHistory: false,
    },
  };
}

function parsePersistedWorkspaceState(raw: string | null): AgentWorkspaceState | null {
  if (!raw) {
    return null;
  }

  const parsed = JSON.parse(raw) as Partial<AgentWorkspaceState> & Partial<Conversation>;

  if (parsed?.conversation?.messages && Array.isArray(parsed.conversation.messages)) {
    return {
      conversation: parsed.conversation,
      input: typeof parsed.input === "string" ? parsed.input : "",
      isTyping: Boolean(parsed.isTyping),
      lastActiveTime:
        typeof parsed.lastActiveTime === "string"
          ? parsed.lastActiveTime
          : parsed.conversation.updatedAt ?? new Date().toISOString(),
      historyRetention: {
        hasTrimmedHistory: Boolean(parsed.historyRetention?.hasTrimmedHistory),
        earliestKeptAt:
          typeof parsed.historyRetention?.earliestKeptAt === "string"
            ? parsed.historyRetention.earliestKeptAt
            : undefined,
      },
    };
  }

  if (parsed?.messages && Array.isArray(parsed.messages)) {
    return {
      conversation: parsed as Conversation,
      input: "",
      isTyping: false,
      lastActiveTime:
        typeof parsed.updatedAt === "string" ? parsed.updatedAt : new Date().toISOString(),
      historyRetention: {
        hasTrimmedHistory: false,
      },
    };
  }

  return null;
}

function readPersistedWorkspaceState() {
  if (typeof window === "undefined") {
    return buildInitialWorkspaceState();
  }

  const raw = window.localStorage.getItem(SINGLE_CONVERSATION_STORAGE_KEY);

  if (!raw) {
    return buildInitialWorkspaceState();
  }

  try {
    return parsePersistedWorkspaceState(raw) ?? buildInitialWorkspaceState();
  } catch (error) {
    console.error("Failed to parse agent workspace state", error);
    return buildInitialWorkspaceState();
  }
}

function persistWorkspaceState(state: AgentWorkspaceState, sourceId?: string) {
  if (typeof window === "undefined") {
    return;
  }

  window.localStorage.setItem(SINGLE_CONVERSATION_STORAGE_KEY, JSON.stringify(state));
  window.dispatchEvent(
    new CustomEvent<AgentWorkspaceSyncDetail>(AGENT_WORKSPACE_SYNC_EVENT, {
      detail: { sourceId, state },
    }),
  );
}

function buildHistoryRetentionNotice(earliestKeptAt?: string) {
  if (!earliestKeptAt) {
    return `仅展示最近 ${HISTORY_RETENTION_DAYS} 天内、最多 ${MAX_PERSISTED_MESSAGES} 条聊天记录。`;
  }

  return `仅展示最近 ${HISTORY_RETENTION_DAYS} 天内、最多 ${MAX_PERSISTED_MESSAGES} 条聊天记录，当前可查看至 ${formatExpiredTime(earliestKeptAt)} 的记录。`;
}

function isSessionExpired(lastActiveTime: string) {
  return Date.now() - new Date(lastActiveTime).getTime() > SESSION_TIMEOUT_MS;
}

function applyHistoryRetention(state: AgentWorkspaceState) {
  const originalMessages = state.conversation.messages;
  if (!originalMessages.length) {
    return state;
  }

  const cutoffTime = Date.now() - HISTORY_RETENTION_DAYS * 24 * 60 * 60 * 1000;
  const recentMessages = originalMessages.filter((message) => {
    const createdAt = new Date(message.createdAt).getTime();
    return Number.isFinite(createdAt) && createdAt >= cutoffTime;
  });

  let keptMessages = recentMessages;
  let trimmedNow = recentMessages.length !== originalMessages.length;

  if (!keptMessages.length) {
    const now = new Date().toISOString();
    keptMessages = [createGreetingMessage(now)];
    trimmedNow = true;
  }

  if (keptMessages.length > MAX_PERSISTED_MESSAGES) {
    keptMessages = keptMessages.slice(-MAX_PERSISTED_MESSAGES);
    trimmedNow = true;
  }

  const hasTrimmedHistory = state.historyRetention.hasTrimmedHistory || trimmedNow;
  const earliestKeptAt = hasTrimmedHistory ? keptMessages[0]?.createdAt : undefined;

  const messagesChanged =
    keptMessages.length !== originalMessages.length ||
    keptMessages.some((message, index) => message !== originalMessages[index]);
  const retentionChanged =
    hasTrimmedHistory !== state.historyRetention.hasTrimmedHistory ||
    earliestKeptAt !== state.historyRetention.earliestKeptAt;

  if (!messagesChanged && !retentionChanged) {
    return state;
  }

  return {
    ...state,
    conversation: {
      ...state.conversation,
      messages: keptMessages,
    },
    historyRetention: {
      hasTrimmedHistory,
      earliestKeptAt,
    },
  };
}

function expireSessionStateIfNeeded(state: AgentWorkspaceState): AgentWorkspaceState {
  if (!state.conversation.sessionId || !isSessionExpired(state.lastActiveTime)) {
    return state;
  }

  const hasPrompt = state.conversation.messages.some((message) => message.type === "expired_prompt");
  if (hasPrompt) {
    return {
      ...state,
      conversation: {
        ...state.conversation,
        sessionId: undefined,
      },
    };
  }

  const now = new Date().toISOString();
  const expiredMessages: ConversationMessage[] = [
    {
      id: createId("msg"),
      role: "system",
      type: "expired_divider",
      content: `上下文已失效·${formatExpiredTime(state.lastActiveTime)}`,
      createdAt: now,
    },
    {
      id: createId("msg"),
      role: "system",
      type: "expired_prompt",
      content: "再次发送消息将开启新对话，无法关联之前的内容",
      createdAt: now,
    },
  ];

  return {
    ...state,
    conversation: {
      ...state.conversation,
      sessionId: undefined,
      messages: [...state.conversation.messages, ...expiredMessages],
    },
  };
}

function formatExpiredTime(value: string) {
  const d = new Date(value);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  const hour = String(d.getHours()).padStart(2, "0");
  const min = String(d.getMinutes()).padStart(2, "0");
  return `${year}/${month}/${day} ${hour}:${min}`;
}

function escapeHtml(content: string) {
  return content
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function highlightCode(content: string) {
  let html = escapeHtml(content);

  html = html.replace(
    /(\/\/.*$|#.*$)/gm,
    '<span class="text-[#7f8ea3]">$1</span>',
  );
  html = html.replace(
    /(".*?"|'.*?'|`.*?`)/g,
    '<span class="text-[#9ECBFF]">$1</span>',
  );
  html = html.replace(
    /\b(const|let|var|function|return|await|async|if|else|for|while|import|from|export|throw|new|try|catch|interface|type)\b/g,
    '<span class="text-[#FF9BD8]">$1</span>',
  );
  html = html.replace(/\b(\d+)\b/g, '<span class="text-[#8FE388]">$1</span>');

  return html;
}

function MarkdownCodeBlock({ code, language = "text", compact = false }: MarkdownCodeBlockProps) {
  const [isCopied, setIsCopied] = useState(false);
  const lines = code.replace(/\n$/, "").split("\n");

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setIsCopied(true);
      window.setTimeout(() => setIsCopied(false), 1800);
    } catch (error) {
      console.error("Failed to copy code", error);
    }
  };

  return (
    <div
      className={clsx(
        "my-4 overflow-hidden rounded-[1.4rem] border border-[#16203E] bg-[#0F172A] shadow-[0_18px_50px_rgba(15,23,42,0.25)]",
        compact && "rounded-[1.2rem]",
      )}
    >
      <div className="flex items-center justify-between border-b border-white/10 bg-white/5 px-4 py-3">
        <span className="rounded-full border border-white/10 bg-white/10 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.2em] text-white/72">
          {language}
        </span>
        <button
          type="button"
          onClick={handleCopy}
          className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/8 px-3 py-1.5 text-xs font-medium text-white/78 transition-colors hover:bg-white/14"
        >
          {isCopied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
          {isCopied ? "已复制" : "复制代码"}
        </button>
      </div>

      <div className="overflow-x-auto px-0 py-3">
        <div className="min-w-full">
          {lines.map((line, index) => (
            <div key={`${language}-${index}`} className="grid grid-cols-[48px_1fr] items-start gap-4 px-4">
              <span className="select-none pt-[1px] text-right font-mono text-xs text-white/28">
                {String(index + 1).padStart(2, "0")}
              </span>
              <code
                className="block whitespace-pre-wrap break-words pb-1 font-mono text-[13px] leading-6 text-white"
                dangerouslySetInnerHTML={{ __html: highlightCode(line || " ") }}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function MermaidBlock({ chart, compact = false }: { chart: string; compact?: boolean }) {
  const containerIdRef = useRef(`mermaid-${createId("chart")}`);
  const [svg, setSvg] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    let cancelled = false;

    const renderChart = async () => {
      try {
        const mermaid = (await import("mermaid")).default;
        const isDark = document.documentElement.classList.contains("dark");

        mermaid.initialize({
          startOnLoad: false,
          securityLevel: "strict",
          theme: isDark ? "dark" : "default",
        });

        const { svg: nextSvg } = await mermaid.render(containerIdRef.current, chart);
        if (!cancelled) {
          setSvg(nextSvg);
          setErrorMessage("");
        }
      } catch (error) {
        console.error("Failed to render mermaid chart", error);
        if (!cancelled) {
          setSvg("");
          setErrorMessage("Mermaid 图表渲染失败，请检查语法。");
        }
      }
    };

    void renderChart();

    return () => {
      cancelled = true;
    };
  }, [chart]);

  return (
    <div
      className={clsx(
        "my-4 overflow-hidden rounded-[1.45rem] border border-[#D8E4FF] bg-[linear-gradient(180deg,rgba(255,255,255,0.97),rgba(245,248,255,0.96))] shadow-[0_18px_42px_rgba(51,90,255,0.08)] dark:border-white/10 dark:bg-[#0f172a]/80 dark:shadow-none",
        compact && "rounded-[1.2rem]",
      )}
    >
      <div className="flex items-center justify-between border-b border-[#E7EEFF] bg-white/72 px-4 py-3 dark:border-white/10 dark:bg-white/[0.03]">
        <span className="rounded-full border border-[#D6E2FF] bg-[#F4F7FF] px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#335AFF] dark:border-white/10 dark:bg-white/[0.05] dark:text-[#AFC0FF]">
          mermaid
        </span>
        <span className="text-xs font-medium text-[#71829C] dark:text-[#94a3b8]">图表预览</span>
      </div>

      <div className="overflow-x-auto px-4 py-4">
        {errorMessage ? (
          <div className="rounded-2xl border border-[#FFD9D6] bg-[#FFF7F6] px-4 py-3 text-sm text-[#B42318] dark:border-[#7A271A] dark:bg-[#2B1615] dark:text-[#FFB4AB]">
            {errorMessage}
          </div>
        ) : svg ? (
          <div
            className="mermaid-preview flex min-w-fit justify-center [&>svg]:h-auto [&>svg]:max-w-full"
            dangerouslySetInnerHTML={{ __html: svg }}
          />
        ) : (
          <div className="flex min-h-[180px] items-center justify-center rounded-2xl border border-dashed border-[#D8E4FF] bg-white/70 text-sm text-[#71829C] dark:border-white/10 dark:bg-white/[0.03] dark:text-[#94a3b8]">
            正在生成 Mermaid 预览...
          </div>
        )}
      </div>
    </div>
  );
}

function AgentMarkdown({ content, compact = false }: { content: string; compact?: boolean }) {
  const components: Components = {
    pre: ({ children }) => <>{children}</>,
    p: ({ children }) => (
      <p
        className={clsx(
          "mt-4 text-sm leading-7 text-[#4F5F7A] dark:text-[#cbd5e1]",
          compact && "text-[13px] leading-6",
        )}
      >
        {children}
      </p>
    ),
    h1: ({ children }) => (
      <h1
        className={clsx(
          "mt-6 text-xl font-semibold tracking-tight text-[#0F172A] dark:text-[#f8fafc]",
          compact && "text-lg",
        )}
      >
        {children}
      </h1>
    ),
    h2: ({ children }) => (
      <h2
        className={clsx(
          "mt-6 text-lg font-semibold tracking-tight text-[#0F172A] dark:text-[#f8fafc]",
          compact && "text-base",
        )}
      >
        {children}
      </h2>
    ),
    h3: ({ children }) => (
      <h3
        className={clsx(
          "mt-5 text-[15px] font-semibold tracking-tight text-[#0F172A] dark:text-[#f8fafc]",
          compact && "text-sm",
        )}
      >
        {children}
      </h3>
    ),
    ul: ({ children }) => (
      <ul
        className={clsx(
          "mt-4 list-disc space-y-2 pl-5 text-sm leading-7 text-[#4F5F7A] dark:text-[#cbd5e1]",
          compact && "text-[13px] leading-6",
        )}
      >
        {children}
      </ul>
    ),
    ol: ({ children }) => (
      <ol
        className={clsx(
          "mt-4 list-decimal space-y-2 pl-5 text-sm leading-7 text-[#4F5F7A] dark:text-[#cbd5e1]",
          compact && "text-[13px] leading-6",
        )}
      >
        {children}
      </ol>
    ),
    li: ({ children }) => <li className="pl-1">{children}</li>,
    blockquote: ({ children }) => (
      <blockquote className="mt-4 rounded-2xl border border-[#DCE6FF] bg-[#F7F9FF] px-4 py-3 text-sm leading-7 text-[#4F5F7A] dark:border-white/10 dark:bg-[#0f172a]/50 dark:text-[#cbd5e1]">
        {children}
      </blockquote>
    ),
    hr: () => <div className="my-5 h-px w-full bg-[#E3EAF7] dark:bg-white/10" />,
    table: ({ children }) => (
      <div className="my-4 overflow-x-auto rounded-[1.2rem] border border-[#DCE5F7] bg-white/88 dark:border-white/10 dark:bg-[#0f172a]/60">
        <table className="min-w-full border-collapse text-left text-sm text-[#364152] dark:text-[#dbe5f4]">
          {children}
        </table>
      </div>
    ),
    thead: ({ children }) => <thead className="bg-[#F5F8FF] dark:bg-white/[0.04]">{children}</thead>,
    th: ({ children }) => (
      <th className="border-b border-[#E5ECF8] px-4 py-3 font-semibold dark:border-white/10">
        {children}
      </th>
    ),
    td: ({ children }) => (
      <td className="border-t border-[#EEF3FA] px-4 py-3 align-top dark:border-white/10">{children}</td>
    ),
    a: ({ href, children }) => (
      <a
        href={href}
        target="_blank"
        rel="noreferrer"
        className="font-medium text-[#335AFF] underline decoration-[#BFD0FF] underline-offset-4"
      >
        {children}
      </a>
    ),
    code: ({ className, children, ...props }) => {
      const languageMatch = /language-([\w-]+)/.exec(className || "");
      const language = languageMatch?.[1]?.toLowerCase() || "";
      const codeContent = String(children).replace(/\n$/, "");
      const isBlock = Boolean(languageMatch);

      if (!isBlock) {
        return (
          <code
            className="rounded-md bg-[#EEF3FF] px-1.5 py-0.5 font-mono text-[0.9em] text-[#2744BE] dark:bg-[#0f172a] dark:text-[#8da4ff]"
            {...props}
          >
            {children}
          </code>
        );
      }

      if (language === "mermaid") {
        return <MermaidBlock chart={codeContent} compact={compact} />;
      }

      return <MarkdownCodeBlock code={codeContent} language={language || "text"} compact={compact} />;
    },
  };

  return (
    <div className={clsx("markdown-message", compact && "markdown-message-compact")}>
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
        {content}
      </ReactMarkdown>
    </div>
  );
}

function Composer({
  value,
  onChange,
  onSend,
  onQuickStart,
  isTyping,
  quickStarts,
  variant,
}: {
  value: string;
  onChange: (value: string) => void;
  onSend: () => void;
  onQuickStart?: (value: string) => void;
  isTyping: boolean;
  quickStarts?: readonly string[];
  variant: "page" | "popup";
}) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (!textareaRef.current || variant !== "page") {
      return;
    }

    textareaRef.current.style.height = "auto";
    const lineHeight = 24;
    const maxHeight = lineHeight * 4 + 12;
    textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, maxHeight)}px`;
  }, [value, variant]);

  if (variant === "popup") {
    return (
      <div className="border-t border-[#E7ECF6] dark:border-white/10 bg-white/95 dark:bg-[#090b11]/95 px-4 py-3">
        <div className="flex items-center gap-2 rounded-[1.35rem] border border-[#D8E0F2] dark:border-white/10 bg-[#F3F7FF] dark:bg-[#1e293b] p-1.5 shadow-[0_14px_30px_rgba(15,23,42,0.05)] dark:shadow-none">
          <div className="flex flex-1 items-center rounded-[1rem] border border-white/80 bg-white/92 px-3 shadow-[inset_0_1px_0_rgba(255,255,255,0.75)] dark:border-white/5 dark:bg-[#0f172a]/70">
            <input
              value={value}
            onChange={(event) => onChange(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter" && !event.shiftKey) {
                  event.preventDefault();
                  onSend();
                }
              }}
              placeholder="描述你的业务场景或提问..."
              className="h-9 flex-1 bg-transparent text-sm text-[#0F172A] dark:text-[#f8fafc] outline-none placeholder:text-[#8B98AE] dark:placeholder:text-[#94a3b8]"
            />
          </div>
          <button
            type="button"
            onClick={onSend}
            disabled={!value.trim() || isTyping}
            className="inline-flex h-10 w-10 items-center justify-center rounded-[1rem] border border-[#C9D8FF] bg-[linear-gradient(135deg,#335AFF_0%,#4F72FF_100%)] text-white shadow-[0_10px_22px_rgba(51,90,255,0.22)] transition-all hover:-translate-y-0.5 hover:shadow-[0_14px_26px_rgba(51,90,255,0.26)] disabled:cursor-not-allowed disabled:opacity-45 disabled:hover:translate-y-0 disabled:hover:shadow-[0_10px_22px_rgba(51,90,255,0.22)] dark:border-white/10"
          >
            <SendHorizonal className="h-4 w-4" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-x-0 bottom-0 z-40 px-3 pb-3 pt-3 md:px-4 md:pb-4 md:pt-4">
      <div className="mx-auto flex w-full max-w-[1600px] justify-center">
        <div className="w-full max-w-[940px]">
        {!!quickStarts?.length && (
          <div className="mb-3 flex flex-wrap gap-2 px-1">
            {quickStarts.map((item) => (
              <button
                key={item}
                type="button"
                onClick={() => onQuickStart?.(item)}
                className="inline-flex items-center gap-2 rounded-full border border-[#DCE7FF] bg-white px-3.5 py-2 text-sm font-medium text-[#3E5381] shadow-[0_10px_24px_rgba(15,23,42,0.04)] transition-all hover:-translate-y-0.5 hover:border-[#BED0FF] hover:bg-[#f9fbff] hover:text-[#2744BE] hover:shadow-[0_14px_28px_rgba(51,90,255,0.08)] dark:border-white/10 dark:bg-[#11182c] dark:text-[#B8C5E2] dark:hover:border-white/20 dark:hover:bg-[#15203a] dark:hover:text-white"
              >
                <MessageSquareText className="h-3.5 w-3.5 text-[#335AFF]" />
                {item}
              </button>
            ))}
          </div>
        )}

        <div className="rounded-[2rem] border border-[#D8E3F8] bg-[#f5f8ff] p-3 shadow-[0_20px_48px_rgba(15,23,42,0.12)] dark:border-white/10 dark:bg-[#0b1020] dark:shadow-[0_18px_40px_rgba(0,0,0,0.32)]">
          <div className="rounded-[1.55rem] border border-[#D3DEF5] bg-white px-3 py-2 shadow-[inset_0_1px_0_rgba(255,255,255,0.92)] dark:border-white/8 dark:bg-[#0f172a]">
            <textarea
              ref={textareaRef}
              rows={1}
              value={value}
            onChange={(event) => onChange(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter" && !event.shiftKey) {
                  event.preventDefault();
                  onSend();
                }
              }}
              placeholder="描述你的业务场景，例如：我要在企业门户中集成智能 PPT 生成能力"
              className="max-h-[108px] min-h-[48px] w-full resize-none bg-transparent px-3 py-2 text-[15px] leading-7 text-[#0F172A] dark:text-[#f8fafc] outline-none placeholder:text-[#8B98AE] dark:placeholder:text-[#94a3b8]"
            />
          </div>

          <div className="mt-3 flex items-center justify-between gap-3 px-1">
            <div className="flex flex-wrap items-center gap-2 text-xs text-[#6C7A92] dark:text-[#9FB0CC]">
              <span className="inline-flex items-center gap-2 rounded-full border border-[#E0E9FF] bg-white/82 px-3 py-1.5 font-medium text-[#5870B6] dark:border-white/10 dark:bg-white/5 dark:text-[#A8B6D8]">
                <Sparkles className="h-3.5 w-3.5" />
                基于官方文档回答
              </span>
              <span className="hidden text-[#8A97AC] dark:text-[#70809E] md:inline">
                Enter 发送，Shift + Enter 换行
              </span>
            </div>

            <button
              type="button"
              onClick={onSend}
              disabled={!value.trim() || isTyping}
              className="inline-flex items-center gap-2 rounded-[1rem] border border-[#C9D8FF] bg-[linear-gradient(135deg,#335AFF_0%,#4F72FF_100%)] px-4 py-2.5 text-sm font-semibold text-white shadow-[0_10px_22px_rgba(51,90,255,0.22)] transition-all hover:-translate-y-0.5 hover:shadow-[0_14px_28px_rgba(51,90,255,0.26)] disabled:cursor-not-allowed disabled:opacity-45 disabled:hover:translate-y-0 disabled:hover:shadow-[0_10px_22px_rgba(51,90,255,0.22)] dark:border-white/10"
            >
              发送
              <SendHorizonal className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      </div>
    </div>
  );
}

function TypingBubble({ compact = false }: { compact?: boolean }) {
  return (
    <div className="flex items-start gap-3">
      <div
        className={clsx(
          "flex h-9 w-9 shrink-0 items-center justify-center rounded-2xl bg-[#335AFF] text-white shadow-[0_10px_24px_rgba(51,90,255,0.28)]",
          compact && "h-8 w-8 rounded-xl",
        )}
      >
        <Bot className={clsx("h-4.5 w-4.5", compact && "h-4 w-4")} />
      </div>

      <div
        className={clsx(
          "inline-flex items-center gap-1.5 rounded-[1.4rem] border border-[#E3E9F4] bg-white px-4 py-3 shadow-[0_10px_28px_rgba(15,23,42,0.05)] dark:border-white/10 dark:bg-[#1e293b] dark:shadow-none",
          !compact && "bg-white/84 backdrop-blur-xl dark:bg-white/[0.04]",
        )}
      >
        <Loader2 className="h-4 w-4 animate-spin text-[#335AFF] dark:text-[#8da4ff]" />
        <span className="text-[13px] font-medium text-[#52637E] dark:text-[#94a3b8]">AI 思考中...</span>
      </div>
    </div>
  );
}

export function AgentWorkspace({ variant, onExpand, onMinimize }: AgentWorkspaceProps) {
  const isPage = variant === "page";
  const instanceIdRef = useRef(createId("agent-workspace"));
  const initialStateRef = useRef<AgentWorkspaceState>(
    typeof window === "undefined"
      ? buildInitialWorkspaceState()
      : applyHistoryRetention(expireSessionStateIfNeeded(readPersistedWorkspaceState())),
  );
  const workspaceStateRef = useRef<AgentWorkspaceState>(initialStateRef.current);
  const isMountedRef = useRef(false);
  const [conversation, setConversation] = useState<Conversation>(() => initialStateRef.current.conversation);
  const [input, setInput] = useState(initialStateRef.current.input);
  const [isTyping, setIsTyping] = useState(initialStateRef.current.isTyping);
  const [historyRetention, setHistoryRetention] = useState(initialStateRef.current.historyRetention);
  const [isAtTop, setIsAtTop] = useState(false);
  const [isNearBottom, setIsNearBottom] = useState(true);
  const [canScrollMessages, setCanScrollMessages] = useState(false);
  const scrollerRef = useRef<HTMLDivElement>(null);

  const syncReactState = useCallback((nextState: AgentWorkspaceState) => {
    setConversation(nextState.conversation);
    setInput(nextState.input);
    setIsTyping(nextState.isTyping);
    setHistoryRetention(nextState.historyRetention);
  }, []);

  const updateWorkspaceState = useCallback(
    (updater: AgentWorkspaceState | ((prev: AgentWorkspaceState) => AgentWorkspaceState)) => {
      const prevState = workspaceStateRef.current;
      const nextState = applyHistoryRetention(
        expireSessionStateIfNeeded(
          typeof updater === "function" ? updater(prevState) : updater,
        ),
      );

      if (Object.is(nextState, prevState)) {
        return;
      }

      workspaceStateRef.current = nextState;
      persistWorkspaceState(nextState, instanceIdRef.current);

      if (isMountedRef.current) {
        syncReactState(nextState);
      }
    },
    [syncReactState],
  );

  useEffect(() => {
    isMountedRef.current = true;
    const initialState = applyHistoryRetention(expireSessionStateIfNeeded(readPersistedWorkspaceState()));
    workspaceStateRef.current = initialState;
    syncReactState(initialState);

    const handleSync = (event: Event) => {
      const detail = (event as CustomEvent<AgentWorkspaceSyncDetail>).detail;
      if (!detail || detail.sourceId === instanceIdRef.current) {
        return;
      }

      const nextState = applyHistoryRetention(expireSessionStateIfNeeded(detail.state));
      workspaceStateRef.current = nextState;
      syncReactState(nextState);
    };

    const handleStorage = (event: StorageEvent) => {
      if (event.key !== SINGLE_CONVERSATION_STORAGE_KEY) {
        return;
      }

      try {
        const nextState = applyHistoryRetention(
          expireSessionStateIfNeeded(
            parsePersistedWorkspaceState(event.newValue) ?? buildInitialWorkspaceState(),
          ),
        );
        workspaceStateRef.current = nextState;
        syncReactState(nextState);
      } catch (error) {
        console.error("Failed to sync agent workspace state", error);
      }
    };

    window.addEventListener(AGENT_WORKSPACE_SYNC_EVENT, handleSync as EventListener);
    window.addEventListener("storage", handleStorage);

    return () => {
      isMountedRef.current = false;
      window.removeEventListener(AGENT_WORKSPACE_SYNC_EVENT, handleSync as EventListener);
      window.removeEventListener("storage", handleStorage);
    };
  }, [syncReactState]);

  useEffect(() => {
    const checkExpiry = () => {
      updateWorkspaceState((prev) => prev);
    };

    checkExpiry();
    const interval = setInterval(checkExpiry, 60 * 1000);
    return () => clearInterval(interval);
  }, [updateWorkspaceState]);

  useEffect(() => {
    if (!scrollerRef.current) {
      return;
    }

    scrollerRef.current.scrollTo({
      top: scrollerRef.current.scrollHeight,
      behavior: "smooth",
    });
  }, [conversation.messages.length, isTyping]);

  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) {
      return;
    }

    const syncScrollTopState = () => {
      setIsAtTop(scroller.scrollTop <= 16);
      setIsNearBottom(scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight <= 24);
      setCanScrollMessages(scroller.scrollHeight > scroller.clientHeight + 24);
    };

    syncScrollTopState();
    scroller.addEventListener("scroll", syncScrollTopState, { passive: true });

    return () => scroller.removeEventListener("scroll", syncScrollTopState);
  }, [conversation.messages.length, isTyping, variant]);

  const showQuickStarts = conversation.messages.length === 1 && conversation.messages[0].role === "assistant";
  const historyRetentionNotice =
    historyRetention.hasTrimmedHistory && isAtTop
      ? buildHistoryRetentionNotice(historyRetention.earliestKeptAt)
      : null;
  const jumpButtonMode = isNearBottom ? "top" : "bottom";

  const clearSessionPrompt = () => {
    updateWorkspaceState((prev) => ({
      ...prev,
      conversation: {
        ...prev.conversation,
        sessionId: undefined,
        messages: prev.conversation.messages.filter((message) => message.type !== "expired_prompt"),
      },
    }));
  };

  const handleInputChange = (value: string) => {
    updateWorkspaceState((prev) => ({
      ...prev,
      input: value,
    }));
  };

  const scrollMessagesToEdge = (edge: "top" | "bottom") => {
    const scroller = scrollerRef.current;
    if (!scroller) {
      return;
    }

    scroller.scrollTo({
      top: edge === "top" ? 0 : scroller.scrollHeight,
      behavior: "smooth",
    });
  };

  const submitMessage = async (preset?: string) => {
    const currentState = applyHistoryRetention(expireSessionStateIfNeeded(workspaceStateRef.current));
    if (currentState !== workspaceStateRef.current) {
      updateWorkspaceState(currentState);
    }
    const currentConversation = currentState.conversation;
    const nextInput = (preset ?? currentState.input).trim();
    if (!nextInput) {
      return;
    }

    const currentSessionId = currentConversation.sessionId;
    let newMessages = [...currentConversation.messages];

    // 清除掉提示条，因为用户已经发新消息了
    newMessages = newMessages.filter((message) => message.type !== "expired_prompt");

    const now = new Date().toISOString();
    const userMessage: ConversationMessage = {
      id: createId("msg"),
      role: "user",
      content: nextInput,
      createdAt: now,
    };

    newMessages.push(userMessage);

    updateWorkspaceState((prev) => ({
      ...prev,
      input: "",
      isTyping: true,
      lastActiveTime: now,
      conversation: {
        ...prev.conversation,
        updatedAt: now,
        messages: newMessages,
        sessionId: currentSessionId,
      },
    }));

    const assistantMessageId = createId("msg");
    let hasReceivedFirstChunk = false;

    try {
      const res = await fetch("/api/agent", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          prompt: nextInput,
          session_id: currentSessionId || "",
        }),
      });

      if (!res.ok) {
        throw new Error(`请求失败: ${res.status}`);
      }

      if (!res.body) {
        throw new Error("无返回内容");
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder("utf-8");
      let done = false;
      let accumulatedContent = "";
      let newSessionId = currentSessionId;
      let buffer = "";

      while (!done) {
        const { value, done: doneReading } = await reader.read();
        done = doneReading;

        if (value) {
          if (!hasReceivedFirstChunk) {
            hasReceivedFirstChunk = true;
            updateWorkspaceState((prev) => ({
              ...prev,
              isTyping: false,
              conversation: {
                ...prev.conversation,
                updatedAt: new Date().toISOString(),
                messages: [
                  ...prev.conversation.messages,
                  {
                    id: assistantMessageId,
                    role: "assistant",
                    content: "",
                    createdAt: new Date().toISOString(),
                  },
                ],
              },
            }));
          }
          buffer += decoder.decode(value, { stream: true });
        }

        const lines = buffer.split("\n");
        buffer = done ? "" : lines.pop() || "";

        for (const line of lines) {
          if (line.startsWith("data:")) {
            try {
              const data = JSON.parse(line.slice(5));
              if (data.output?.text) {
                accumulatedContent = data.output.text;
              }
              if (data.output?.session_id) {
                newSessionId = data.output.session_id;
              }
            } catch {
              // 忽略解析错误
            }
          }
        }

        updateWorkspaceState((prev) => ({
          ...prev,
          conversation: {
            ...prev.conversation,
            sessionId: newSessionId,
            updatedAt: new Date().toISOString(),
            messages: prev.conversation.messages.map((message) =>
              message.id === assistantMessageId
                ? { ...message, content: accumulatedContent }
                : message,
            ),
          },
        }));
      }
    } catch (error: unknown) {
      console.error(error);
      if (!hasReceivedFirstChunk) {
        updateWorkspaceState((prev) => ({
          ...prev,
          isTyping: false,
          conversation: {
            ...prev.conversation,
            updatedAt: new Date().toISOString(),
            messages: [
              ...prev.conversation.messages,
              {
                id: assistantMessageId,
                role: "assistant",
                content: "抱歉，请求失败，请稍后再试。",
                createdAt: new Date().toISOString(),
              },
            ],
          },
        }));
      } else {
        updateWorkspaceState((prev) => ({
          ...prev,
          conversation: {
            ...prev.conversation,
            updatedAt: new Date().toISOString(),
            messages: prev.conversation.messages.map((message) =>
              message.id === assistantMessageId
                ? { ...message, content: "抱歉，请求失败，请稍后再试。" }
                : message,
            ),
          },
        }));
      }
    } finally {
      updateWorkspaceState((prev) => ({
        ...prev,
        isTyping: false,
      }));
    }
  };

  if (!conversation) {
    return null;
  }

  const messageList = (
    <div
      ref={scrollerRef}
      className={clsx(
        "hide-scrollbar flex-1 overflow-y-auto",
        isPage ? "px-4 py-4 pb-44 md:px-7 md:py-5 md:pb-48" : "px-4 py-4",
      )}
    >
      <div className={clsx("mx-auto space-y-6", isPage ? "max-w-[1080px]" : "max-w-none space-y-5")}>
        {historyRetentionNotice && (
          <div
            className={clsx(
              "sticky top-0 z-10 rounded-2xl border px-4 py-3 text-sm shadow-sm backdrop-blur-xl",
              isPage
                ? "border-[#D9E4FA] bg-white/88 text-[#51627D] dark:border-white/10 dark:bg-[#0f172a]/82 dark:text-[#A8B6D8]"
                : "border-[#E1E8F5] bg-white/94 text-[#5A6A84] dark:border-white/10 dark:bg-[#111827]/92 dark:text-[#9FB0CC]",
            )}
          >
            <div className="flex items-start gap-2">
              <Info className="mt-0.5 h-4 w-4 shrink-0 text-[#335AFF]" />
              <p className="leading-6">{historyRetentionNotice}</p>
            </div>
          </div>
        )}

        {!isPage && showQuickStarts && (
          <div
            className={clsx(
              isPage
                ? "relative overflow-hidden rounded-[2.2rem] border border-white/65 bg-white/[0.56] px-6 py-6 shadow-[0_22px_55px_rgba(15,23,42,0.05)] backdrop-blur-xl dark:border-white/10 dark:bg-white/[0.03] dark:shadow-none"
                : "overflow-hidden rounded-[2rem] border border-[#E1E8F5] bg-[#F8FAFF] p-5 shadow-[0_16px_38px_rgba(15,23,42,0.04)] dark:border-white/10 dark:bg-[#111827] dark:shadow-none",
            )}
          >
            {isPage && (
              <div className="pointer-events-none absolute inset-0">
                <div className="absolute left-[-6%] top-[-24%] h-44 w-44 rounded-full bg-[#335AFF]/10 blur-3xl" />
                <div className="absolute right-[-10%] top-0 h-36 w-36 rounded-full bg-[#8EA2FF]/14 blur-3xl" />
              </div>
            )}

            <div className="relative flex items-start gap-3">
              <div
                className={clsx(
                  "inline-flex items-center justify-center bg-[#335AFF] text-white",
                  isPage
                    ? "h-12 w-12 rounded-[1.35rem] shadow-[0_16px_36px_rgba(51,90,255,0.2)]"
                    : "h-11 w-11 rounded-[1.1rem] shadow-[0_12px_24px_rgba(51,90,255,0.18)]",
                )}
              >
                <Sparkles className="h-5 w-5" />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-[#0F172A] dark:text-[#f8fafc]">从文档场景快速开始</p>
                <p
                  className={clsx(
                    "mt-1 max-w-2xl text-sm leading-7 text-[#52637E] dark:text-[#A5B4CF]",
                    isPage && "max-w-3xl text-[15px] text-[#52607A] dark:text-[#A8B6D8]",
                  )}
                >
                  我可以帮你梳理接口、查字段、写对接流程。点击任一场景后，会直接生成一轮方案建议。
                </p>
              </div>
            </div>

            <div className={clsx("relative mt-5 grid gap-3", isPage ? "md:grid-cols-3" : "grid-cols-1")}>
              {QUICK_STARTS.map((item) => (
                <button
                  key={item}
                  type="button"
                  onClick={() => submitMessage(item)}
                  className={clsx(
                    "rounded-[1.4rem] border px-4 py-4 text-left transition-all",
                    isPage
                      ? "border-white/80 bg-white/84 shadow-[0_12px_28px_rgba(51,90,255,0.06)] hover:-translate-y-0.5 hover:border-[#BCD0FF] hover:bg-white hover:shadow-[0_16px_34px_rgba(51,90,255,0.09)] dark:border-white/10 dark:bg-white/[0.04] dark:hover:border-white/20 dark:hover:bg-white/[0.06]"
                      : "border-[#D8E5FF] bg-white hover:-translate-y-0.5 hover:border-[#BBD0FF] hover:shadow-[0_14px_28px_rgba(51,90,255,0.08)] dark:border-white/10 dark:bg-[#1e293b] dark:hover:border-white/20 dark:hover:shadow-none",
                  )}
                >
                  <div className="flex items-center gap-2 text-sm font-semibold text-[#0F172A] dark:text-[#f8fafc]">
                    <MessageSquareText className="h-4 w-4 text-[#335AFF]" />
                    {item}
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {conversation.messages.map((message) => {
          if (message.role === "system") {
            if (message.type === "expired_divider") {
              return (
                <div key={message.id} className="flex items-center justify-center my-6">
                  <div className="h-px bg-[#E6ECF7] dark:bg-white/10 flex-1 max-w-[120px]"></div>
                  <span className="mx-4 text-xs font-medium text-[#8A97AC] dark:text-[#64748b]">
                    {message.content}
                  </span>
                  <div className="h-px bg-[#E6ECF7] dark:bg-white/10 flex-1 max-w-[120px]"></div>
                </div>
              );
            }
            if (message.type === "expired_prompt") {
              return (
                <div key={message.id} className="flex items-center justify-between mx-auto max-w-[85%] rounded-xl bg-[#F8FAFF] dark:bg-[#1e293b]/50 border border-[#E4EAF4] dark:border-white/5 px-4 py-3 shadow-sm">
                  <div className="flex items-center gap-2">
                    <Info className="h-4 w-4 text-[#335AFF]" />
                    <span className="text-[13px] text-[#52637E] dark:text-[#94a3b8]">
                      {message.content}
                    </span>
                  </div>
                  <button 
                    onClick={clearSessionPrompt}
                    className="text-[13px] font-medium text-[#335AFF] hover:text-[#2848d9] transition-colors"
                  >
                    知道了，清除关联
                  </button>
                </div>
              );
            }
            return null;
          }

          return (
            <div
              key={message.id}
              className={clsx(
                "flex items-start gap-3",
                message.role === "user" && "justify-end",
              )}
            >
              {message.role === "assistant" && (
                <div
                  className={clsx(
                    "flex shrink-0 items-center justify-center bg-[#335AFF] text-white",
                    isPage
                      ? "mt-1 h-9 w-9 rounded-[1rem] shadow-[0_14px_34px_rgba(51,90,255,0.18)]"
                      : "h-8 w-8 rounded-xl shadow-[0_14px_34px_rgba(51,90,255,0.24)]",
                  )}
                >
                  <Bot className={clsx("h-4.5 w-4.5", !isPage && "h-4 w-4")} />
                </div>
              )}

              <div
                className={clsx(
                  isPage ? "max-w-[86%]" : "max-w-[82%]",
                  message.role === "assistant"
                    ? clsx(
                        isPage
                          ? "max-w-[min(920px,calc(100%-3.5rem))] rounded-[1.7rem] border border-[#D9E3F4] bg-[linear-gradient(180deg,rgba(255,255,255,0.98),rgba(247,250,255,0.96))] px-5 py-4 shadow-[0_16px_34px_rgba(15,23,42,0.045)] backdrop-blur-xl dark:border-white/10 dark:bg-white/[0.045] dark:shadow-none"
                          : "rounded-[1.35rem] border border-[#E4EAF4] bg-white px-4 py-3 shadow-[0_12px_32px_rgba(15,23,42,0.05)] dark:border-white/10 dark:bg-[#1e293b] dark:shadow-none",
                      )
                    : clsx(
                        "rounded-[1.5rem] border",
                        isPage
                          ? "border-[#5D7FFF] bg-[linear-gradient(135deg,#4D72FF_0%,#335AFF_100%)] px-4 py-3 text-white shadow-[0_18px_38px_rgba(51,90,255,0.22)]"
                          : "rounded-[1.35rem] border-[#C9D8FF] bg-[linear-gradient(135deg,#335AFF_0%,#4E72FF_100%)] px-4 py-3 text-white shadow-[0_18px_34px_rgba(51,90,255,0.24)] dark:shadow-none",
                      ),
                  !isPage && "max-w-[88%]",
                )}
              >
                {message.role === "assistant" ? (
                  !message.content ? (
                    <div className="flex h-6 items-center gap-1.5">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#335AFF]/60 animate-bounce [animation-delay:-0.3s]"></div>
                      <div className="w-1.5 h-1.5 rounded-full bg-[#335AFF]/60 animate-bounce [animation-delay:-0.15s]"></div>
                      <div className="w-1.5 h-1.5 rounded-full bg-[#335AFF]/60 animate-bounce"></div>
                    </div>
                  ) : (
                    <AgentMarkdown content={message.content} compact={!isPage} />
                  )
                ) : (
                  <p
                    className={clsx(
                      "whitespace-pre-wrap text-sm leading-7",
                      isPage &&
                        (message.role === "user"
                          ? "text-[15px] leading-7 text-white"
                          : "text-[15px] leading-7 text-[#17325C]"),
                      !isPage && "text-[13px] leading-6",
                    )}
                  >
                    {message.content}
                  </p>
                )}
              </div>
            </div>
          );
        })}

        {isTyping && <TypingBubble compact={!isPage} />}
      </div>
    </div>
  );

  if (!isPage) {
    return (
      <div className="flex h-full flex-col overflow-hidden rounded-[2.25rem] border border-[#e1e8f5] dark:border-[#1e293b]/60 bg-[linear-gradient(180deg,rgba(248,250,255,0.95),rgba(242,246,253,0.98))] dark:bg-[linear-gradient(180deg,rgba(9,11,17,0.85),rgba(9,11,17,0.95))] p-5 shadow-[inset_0_1px_0_rgba(255,255,255,0.9)] dark:shadow-[inset_0_1px_0_rgba(255,255,255,0.05),0_18px_40px_rgba(0,0,0,0.4)] lg:p-6 backdrop-blur-xl">
        <div className="flex items-center justify-between border-b border-[#E7ECF6] dark:border-white/10 bg-white/90 dark:bg-[#090b11]/90 px-4 py-3">
          <div className="flex items-center gap-3">
            <div className="inline-flex h-10 w-10 items-center justify-center rounded-[1rem] bg-[#335AFF] text-white shadow-[0_14px_28px_rgba(51,90,255,0.24)]">
              <Bot className="h-4.5 w-4.5" />
            </div>
            <div>
              <div className="text-sm font-semibold text-[#0F172A] dark:text-[#f8fafc]">AI接入助手</div>
              <div className="flex items-center gap-2 text-xs text-[#5A6A84] dark:text-[#94a3b8]">
                <span className="h-2 w-2 rounded-full bg-[#22C55E] shadow-[0_0_0_4px_rgba(34,197,94,0.14)]" />
                在线
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onMinimize}
              className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-[#E4EAF4] dark:border-white/10 bg-white dark:bg-[#1e293b] text-[#5A6A84] dark:text-[#94a3b8] transition-colors hover:text-[#0F172A] dark:hover:text-[#f8fafc] lg:hidden"
              aria-label="最小化"
            >
              <Minimize2 className="h-[18px] w-[18px]" />
            </button>
            <button
              type="button"
              onClick={onExpand}
              className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-[#E4EAF4] dark:border-white/10 bg-white dark:bg-[#1e293b] text-[#5A6A84] dark:text-[#94a3b8] transition-colors hover:text-[#0F172A] dark:hover:text-[#f8fafc]"
              aria-label="展开完整对话"
            >
              <Maximize2 className="h-[18px] w-[18px]" />
            </button>
          </div>
        </div>

        {messageList}
        <Composer
          value={input}
          onChange={handleInputChange}
          onSend={() => submitMessage()}
          isTyping={isTyping}
          variant={variant}
        />
      </div>
    );
  }

  return (
    <section className="relative mx-auto flex h-full min-h-0 w-full max-w-[1600px] flex-1 px-2 pb-2 pt-0 md:px-4 md:pb-4">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-white/70 via-white/20 to-transparent dark:from-[#04060b]/40 dark:via-transparent" />
      <div className="pointer-events-none absolute left-[8%] top-20 h-56 w-56 rounded-full bg-[#335AFF]/10 blur-3xl" />
      <div className="pointer-events-none absolute right-[10%] top-10 h-52 w-52 rounded-full bg-[#93A8FF]/12 blur-3xl" />

      <div className="flex h-full min-h-0 w-full min-w-0">
        <div className="flex min-w-0 flex-1 flex-col">
          {messageList}
          <Composer
            value={input}
            onChange={handleInputChange}
            onSend={() => submitMessage()}
            onQuickStart={submitMessage}
            isTyping={isTyping}
            quickStarts={showQuickStarts ? QUICK_STARTS : undefined}
            variant={variant}
          />
        </div>
      </div>

      {canScrollMessages && (
        <button
          type="button"
          onClick={() => scrollMessagesToEdge(jumpButtonMode)}
          className="fixed bottom-24 right-5 z-50 inline-flex items-center gap-2 rounded-full border border-[#C7D8FF] bg-white px-3.5 py-3 text-sm font-semibold text-[#335AFF] shadow-[0_18px_40px_rgba(51,90,255,0.2)] transition-all hover:-translate-y-0.5 hover:border-[#AFC6FF] hover:text-[#2547d4] hover:shadow-[0_20px_44px_rgba(51,90,255,0.24)] dark:border-white/10 dark:bg-[#0f172a] dark:text-[#9bb0ff] dark:shadow-[0_16px_34px_rgba(0,0,0,0.32)] md:bottom-28 md:right-8"
        >
          {jumpButtonMode === "top" ? <ArrowUp className="h-4.5 w-4.5" /> : <ArrowDown className="h-4.5 w-4.5" />}
          <span>{jumpButtonMode === "top" ? "回到顶部" : "回到底部"}</span>
        </button>
      )}
    </section>
  );
}
